import { Search } from "lucide-react";
import {
  getMethodBadgeClass,
  responseFormat,
  formatBadgeClass,
  groupEndpoints,
  filterEndpoints
} from "./utils";

/**
 * One endpoint row.
 *
 * Shows the response format and the path alongside the name. Providers publish
 * the same report as JSON/HTML/PDF on sibling paths, and with a name alone
 * those rows truncate to identical strings in a narrow column — the format
 * badge plus the path is what keeps them apart.
 */
export const EndpointListItem = ({ endpoint, isSelected, onClick }) => {
  const fmt = responseFormat(endpoint);

  return (
    <button
      type="button"
      onClick={() => onClick(endpoint)}
      aria-current={isSelected ? "true" : undefined}
      className={`w-full text-left px-3 py-2.5 transition-colors border-l-[3px] ${
        isSelected
          ? "bg-blue-50 border-blue-600"
          : "bg-white border-transparent hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={getMethodBadgeClass(endpoint.method, false)}>
          {endpoint.method}
        </span>
        <span
          className={`text-sm font-medium truncate flex-1 min-w-0 ${
            isSelected ? "text-blue-700" : "text-gray-800"
          }`}
        >
          {endpoint.name || endpoint.path}
        </span>
        {fmt !== "JSON" && (
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${formatBadgeClass(
              fmt
            )}`}
          >
            {fmt}
          </span>
        )}
      </div>
      <div className="text-[11px] font-mono text-gray-400 truncate mt-0.5">
        {endpoint.path}
      </div>
    </button>
  );
};

/**
 * Filterable, grouped endpoint list. A flat alphabetical run stops working once
 * a provider ships 30+ endpoints.
 */
export const EndpointBrowser = ({
  endpoints,
  query,
  onQueryChange,
  selectedId,
  onSelect
}) => {
  const visible = filterEndpoints(endpoints, query);
  const groups = groupEndpoints(visible);

  return (
    <>
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Filter by name or path…"
          className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
        />
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">
          Nothing matches “{query}”.
        </p>
      ) : (
        groups.map((g) => (
          <div key={g.label} className="mb-4">
            <div className="flex items-center gap-2 px-1.5 pb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {g.label}
              </span>
              <span className="text-[10px] text-gray-400 bg-gray-100 rounded-full px-1.5">
                {g.endpoints.length}
              </span>
            </div>
            {/* One panel per group, a rule between rows, so neighbouring endpoints don't run together. */}
            <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {g.endpoints.map((ep) => (
                <EndpointListItem
                  key={ep.endpoint_id}
                  endpoint={ep}
                  isSelected={selectedId === ep.endpoint_id}
                  onClick={onSelect}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );
};

export const LoadingSpinner = ({ message }) => (
  <div className="text-sm text-gray-500 text-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
    {message}
  </div>
);

export const ErrorMessage = ({ message }) => (
  <div className="text-sm text-red-600 text-center py-4 bg-red-50 rounded-lg">
    {message}
  </div>
);

export const EmptyState = ({ message, submessage }) => (
  <p className="text-sm text-gray-500 text-center py-4">
    {message}
    {submessage && <span className="block text-xs mt-1">{submessage}</span>}
  </p>
);
